/**
 * 节点画布 · 音频
 * - 参考音频上传：POST /node_upload_audio（FastMovieController，固定返回 audio_url）
 * - 兼容链路：/yinpin_copy + /yinpin_hecheng（与 VideoCopy.vue 一致）
 * - 豆包语音：OpenSpeech v3 异步 TTS、声音复刻上传（经后端 /volc_speech_* 代理）
 * 新版控制台 API Key：https://www.volcengine.com/docs/6561/1816214?lang=zh（后端 X-Api-Key）
 * 语音合成 V3 总览：https://www.volcengine.com/docs/6561/1329505?lang=zh
 * HTTP 异步长文本 submit/query：https://www.volcengine.com/docs/6561/1829010?lang=zh
 */

import api from '@/utils/api'

function pickPayload(res) {
  if (res && typeof res === 'object' && res.data != null) return res.data
  return res
}

/** 从对象上取音频 URL 字段 */
function pickAudioUrlFromObject(o) {
  if (!o || typeof o !== 'object') return ''
  for (const k of ['audio_url', 'url', 'audioUrl', 'download_url']) {
    const v = o[k]
    if (typeof v === 'string' && /^https?:\/\//i.test(v.trim())) return v.trim()
  }
  return ''
}

/**
 * 解析 /volc_speech_tts 响应：兼容 axios 体、网关多包一层 data、或 JSON 字符串
 */
function findVolcSpeechTtsAudioUrl(raw) {
  let cur = raw
  for (let i = 0; i < 6 && cur != null; i++) {
    if (typeof cur === 'string') {
      try {
        cur = JSON.parse(cur)
      } catch (e) {
        return ''
      }
    }
    const u = pickAudioUrlFromObject(cur)
    if (u) return u
    if (cur && typeof cur === 'object' && cur.data != null) {
      cur = cur.data
      continue
    }
    break
  }
  return ''
}

function volcSpeechBusinessCode(res) {
  if (!res || typeof res !== 'object') return null
  if (res.code !== undefined && res.code !== null) return Number(res.code)
  if (res.data && typeof res.data === 'object' && res.data.code !== undefined && res.data.code !== null) {
    return Number(res.data.code)
  }
  return null
}

/** V3 文档：句前/句中表现力用半角 []；输入法常打出全角 【】、［］，提交前统一为半角。 */
export function normalizeVolcTtsBracketText(s) {
  return String(s || '')
    .replace(/\u3010/g, '[')
    .replace(/\u3011/g, ']')
    .replace(/\uFF3B/g, '[')
    .replace(/\uFF3D/g, ']')
}

function looksLikeHttpUrl(s) {
  return typeof s === 'string' && /^https?:\/\//i.test(String(s).trim())
}

function looksLikeDataAudioUrl(s) {
  return typeof s === 'string' && /^data:audio\//i.test(String(s).trim())
}

/** 从整段 JSON 文本里抠出第一个 http(s) 链接（应对字段名不统一、深层嵌套） */
function scrapeFirstHttpUrlFromSerializedJson(body) {
  try {
    const s = typeof body === 'string' ? body : JSON.stringify(body)
    const m = s.match(/https?:\/\/[^"'}\s>\\]+/i)
    if (!m) return ''
    return String(m[0])
      .replace(/[,;.)'"\]}>]+$/, '')
      .trim()
  } catch (e) {
    return ''
  }
}

/** 相对路径 /xxx 或 //cdn/xxx 补成可请求的绝对地址（浏览器环境） */
function absolutizeMediaUrl(u) {
  let t = String(u || '').trim()
  if (!t) return ''
  if (looksLikeHttpUrl(t)) return t
  if (looksLikeDataAudioUrl(t)) return t
  if (t.startsWith('//') && typeof window !== 'undefined' && window.location && window.location.protocol) {
    return window.location.protocol + t
  }
  if (t.startsWith('/') && typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin + t
  }
  /* 无前导斜杠的站点相对路径，如 uploads/xxx.mp3 */
  if (
    typeof window !== 'undefined' &&
    window.location &&
    window.location.origin &&
    /^[\w.-]+\/[\w./-]+\.(mp3|wav|m4a|aac|ogg|opus|webm|flac)$/i.test(t)
  ) {
    return `${window.location.origin}/${t.replace(/^\//, '')}`
  }
  return ''
}

/**
 * 从 /video_file 等上传接口的 JSON 中解析媒体地址。
 * 注意：项目 axios 拦截器已返回 response.data，入参即为接口体（不要再多包一层 .data）。
 * @param {unknown} body
 * @param {number} [nest]
 */
export function pickVideoFileUploadUrl(body, nest = 0) {
  if (nest > 6) return ''
  if (body == null) return ''
  if (typeof body === 'string') {
    const raw = String(body).trim()
    if ((raw.startsWith('{') && raw.endsWith('}')) || (raw.startsWith('[') && raw.endsWith(']'))) {
      try {
        const j = JSON.parse(raw)
        const u = pickVideoFileUploadUrl(j, nest)
        if (u) return u
      } catch (e) {
        /* 非 JSON 字符串 */
      }
    }
    const abs = absolutizeMediaUrl(raw)
    if (abs) return abs
    return scrapeFirstHttpUrlFromSerializedJson(raw)
  }
  if (typeof body !== 'object') return ''

  const preferredKeys = [
    'audio_url',
    'url',
    'audioUrl',
    'file_url',
    'video_url',
    'download_url',
    'result_url',
    'href',
    'src',
    'link',
    'media_url',
    'oss_url',
    'cdn_url',
    'full_url',
    'public_url',
    'file_path',
    'audio_path',
    'path',
    'uri',
    'play_url',
    'playUrl',
    'signed_url',
    'resource_url',
    'resourceUrl',
    'file'
  ]

  function tryFlat(o) {
    if (!o || typeof o !== 'object') return ''
    for (const k of preferredKeys) {
      if (!Object.prototype.hasOwnProperty.call(o, k)) continue
      const v = o[k]
      if (typeof v !== 'string') continue
      const abs = absolutizeMediaUrl(v)
      if (abs) return abs
    }
    return ''
  }

  let u = tryFlat(body)
  if (u) return u

  if (body.image && typeof body.image === 'object') {
    u = tryFlat(body.image) || pickVideoFileUploadUrl(body.image, nest + 1)
    if (u) return u
  }

  if (body.data != null) {
    if (typeof body.data === 'string') {
      u = absolutizeMediaUrl(body.data)
      if (u) return u
    } else if (typeof body.data === 'object') {
      u = tryFlat(body.data)
      if (u) return u
      u = pickVideoFileUploadUrl(body.data, nest + 1)
      if (u) return u
    }
  }

  if (body.result && typeof body.result === 'object') {
    u = tryFlat(body.result) || pickVideoFileUploadUrl(body.result, nest + 1)
    if (u) return u
  }

  const skipKeys = new Set(['message', 'msg', 'error', 'errors', 'stack'])
  const seen = new Set()
  function dfs(o, depth) {
    if (!o || depth > 10) return ''
    if (typeof o !== 'object') return ''
    if (seen.has(o)) return ''
    seen.add(o)
    if (Array.isArray(o)) {
      for (const it of o) {
        if (typeof it === 'string') {
          const a = absolutizeMediaUrl(it)
          if (a) return a
        } else {
          const a = dfs(it, depth + 1)
          if (a) return a
        }
      }
      return ''
    }
    for (const k of preferredKeys) {
      if (!Object.prototype.hasOwnProperty.call(o, k)) continue
      const a = dfs(o[k], depth + 1)
      if (a) return a
    }
    for (const k of Object.keys(o)) {
      if (skipKeys.has(k) || preferredKeys.includes(k)) continue
      const v = o[k]
      if (typeof v === 'string') {
        const a = absolutizeMediaUrl(v)
        if (a) return a
        if (looksLikeDataAudioUrl(v)) return v.trim()
      } else if (v && typeof v === 'object') {
        const a = dfs(v, depth + 1)
        if (a) return a
      }
    }
    return ''
  }

  const out = dfs(body, 0)
  if (out) return out
  const scraped = scrapeFirstHttpUrlFromSerializedJson(body)
  if (scraped && looksLikeHttpUrl(scraped)) return scraped
  return ''
}

export async function uploadNodeAudioFile(file) {
  if (!file || !file.type || !file.type.startsWith('audio/')) {
    throw new Error('请选择音频文件')
  }
  const fd = new FormData()
  fd.append('file', file)
  fd.append('type', '2')
  fd.append('filename', file.name)
  fd.append('file_name', file.name)
  fd.append('name', file.name)
  /* 与 uploadImage 一致：同源 /uploads/... 路径，便于 devServer 代理与线上 public */
  fd.append('relative', '1')
  let res = await api.post('/node_upload_audio', fd)
  if (typeof res === 'string') {
    const t = res.trim()
    if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
      try {
        res = JSON.parse(t)
      } catch (e) {
        /* 保持字符串 */
      }
    }
  }
  const url = pickVideoFileUploadUrl(res)
  if (!url) {
    const code = res && typeof res === 'object' && res.code !== undefined ? Number(res.code) : NaN
    const okish =
      code === 0 ||
      code === 200 ||
      (res && typeof res === 'object' && (res.success === true || res.status === 1 || res.status === '1')) ||
      (res && typeof res === 'object' && String(res.msg || '').toLowerCase() === 'success')
    const tail =
      '请在后端 /video_file（MovieController@uploadFile）成功分支里返回 http(s) 的 url 或 audio_url（或嵌套在 data 内）；也可把 Network 响应体贴给前端对齐字段名。'
    if (okish) {
      throw new Error(`上传已成功，但响应里未找到可访问的文件地址。${tail}`)
    }
    const msg = res?.message || res?.msg || res?.error || '上传失败'
    const base = typeof msg === 'string' ? msg : JSON.stringify(msg)
    throw new Error(`${base}（未解析到 url） ${tail}`)
  }
  return url
}

/**
 * @param {{ referenceAudioUrl: string, text: string }} opts
 * @returns {Promise<string>} 合成后的 audio_url
 */
export async function synthesizeClonedVoice(opts) {
  const ref = String(opts?.referenceAudioUrl || '').trim()
  const text = String(opts?.text || '').trim()
  if (!ref) throw new Error('缺少参考音频（请先上传或接入上游音频节点）')
  if (!text) throw new Error('请输入要合成的文本')

  const copyRes = await api.post('/yinpin_copy', { audio_url: ref })
  const voiceId = copyRes?.data?.voice_id || copyRes?.voice_id
  if (!voiceId) {
    const msg =
      copyRes?.error ||
      copyRes?.msg ||
      copyRes?.message ||
      copyRes?.data?.message ||
      '音色初始化失败'
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }

  const hechengRes = await api.post('/yinpin_hecheng', {
    voice_id: voiceId,
    content: text,
    text
  })
  const audioUrl = hechengRes?.data?.audio_url || hechengRes?.audio_url
  if (!audioUrl) {
    const msg =
      hechengRes?.error ||
      hechengRes?.msg ||
      hechengRes?.message ||
      hechengRes?.data?.message ||
      '音频合成失败'
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  return String(audioUrl).trim()
}

/**
 * 豆包语音合成（公版音色或已注册的复刻 speaker_id）
 * @param {{
 *   text: string,
 *   speaker?: string,
 *   resourceId?: string,
 *   ssml?: string,
 *   audioFormat?: string,
 *   sampleRate?: number,
 *   ttsSubModel?: string,
 *   voiceInstruction?: string,
 *   priorContext?: string,
 *   pitchBias?: number,
 *   speedBias?: number,
 *   volumeBias?: number
 * }} opts
 * @returns {Promise<string>} audio_url
 */
export async function synthesizeVolcDoubaoTts(opts) {
  const text = normalizeVolcTtsBracketText(String(opts?.text || '')).trim()
  if (!text) throw new Error('请输入要合成的文本')
  const vi =
    opts?.voiceInstruction != null && String(opts.voiceInstruction).trim()
      ? normalizeVolcTtsBracketText(String(opts.voiceInstruction).trim()).trim()
      : ''
  const pc =
    opts?.priorContext != null && String(opts.priorContext).trim()
      ? normalizeVolcTtsBracketText(String(opts.priorContext).trim()).trim()
      : ''
  const pb = Number(opts?.pitchBias)
  const sb = Number(opts?.speedBias)
  const vb = Number(opts?.volumeBias)
  const body = {
    text,
    ...(opts?.speaker ? { speaker: String(opts.speaker).trim() } : {}),
    ...(opts?.resourceId ? { resource_id: String(opts.resourceId).trim() } : {}),
    ...(opts?.ssml
      ? { ssml: normalizeVolcTtsBracketText(String(opts.ssml)) }
      : {}),
    ...(opts?.audioFormat ? { audio_format: opts.audioFormat } : {}),
    ...(opts?.sampleRate ? { sample_rate: opts.sampleRate } : {}),
    ...(opts?.ttsSubModel ? { tts_sub_model: String(opts.ttsSubModel).trim() } : {}),
    ...(vi ? { voice_instruction: vi } : {}),
    ...(pc ? { prior_context: pc } : {}),
    ...(Number.isFinite(pb) && pb !== 0 ? { pitch_bias: Math.round(pb) } : {}),
    ...(Number.isFinite(sb) && sb !== 0 ? { speed_bias: Math.round(sb) } : {}),
    ...(Number.isFinite(vb) && vb !== 0 ? { volume_bias: Math.round(vb) } : {})
  }
  const res = await api.post('/volc_speech_tts', body)
  const bc = volcSpeechBusinessCode(res)
  if (bc != null && !Number.isNaN(bc) && bc !== 0) {
    const inner = res?.data && typeof res.data === 'object' ? res.data : res
    const errMsg =
      inner?.message || res?.message || inner?.msg || res?.msg || inner?.error || '豆包语音接口返回错误'
    throw new Error(typeof errMsg === 'string' ? errMsg : String(errMsg))
  }
  const url = findVolcSpeechTtsAudioUrl(res)
  if (!url) {
    throw new Error(
      '未解析到音频地址：请检查网关是否多包了一层 data，或联系后端打印 /volc_speech_tts 原始 JSON'
    )
  }
  return url
}

/**
 * 豆包声音复刻：上传参考音频 + 对照文本，返回 speaker_id（后续 TTS 使用 resource seed-icl-2.0）
 * @param {{
 *   referenceAudioUrl?: string,
 *   audioBase64?: string,
 *   text: string,
 *   speakerId?: string,
 *   audioFormat?: string,
 *   modelType?: number,
 *   resourceId?: string
 * }} opts
 * @returns {Promise<{ speakerId: string, raw?: object }>}
 */
export async function registerVolcDoubaoCloneVoice(opts) {
  const text = String(opts?.text || '').trim()
  if (!text) throw new Error('请填写与参考音频一致的朗读文本（用于复刻校验）')

  const ref = String(opts?.referenceAudioUrl || '').trim()
  const b64 = String(opts?.audioBase64 || '').trim()
  if (!ref && !b64) throw new Error('缺少参考音频（audio_url 或 base64）')

  const body = {
    text,
    ...(ref ? { audio_url: ref } : { audio_bytes: b64 }),
    ...(opts?.speakerId ? { speaker_id: String(opts.speakerId).trim() } : {}),
    ...(opts?.audioFormat ? { audio_format: opts.audioFormat } : {}),
    ...(opts?.modelType != null ? { model_type: opts.modelType } : {}),
    ...(opts?.resourceId ? { resource_id: String(opts.resourceId).trim() } : {})
  }
  const res = await api.post('/volc_speech_clone_register', body)
  const data = pickPayload(res)
  const speakerId = data?.speaker_id || data?.speakerId
  if (!speakerId) {
    const msg = res?.message || res?.msg || '声音复刻注册未返回 speaker_id'
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  return { speakerId: String(speakerId).trim(), raw: data?.upstream || data }
}
